'use client';

import { useCallback, useMemo, useRef } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { InventoryItemType } from '@/lib/services/inventory-types';
import {
  groupInventoryTypesByCategory,
  INVENTORY_TYPE_DESCRIPTIONS
} from '@/lib/constants';
import { InventoryIcon } from '../../core/InventoryIcon';
import type { AssetType } from '../../types';

interface TypeSelectionViewProps {
  inventoryTypes: InventoryItemType[];
  loading: boolean;
  onTypeSelect: (typeId: string) => void;
  category?: 'asset' | 'liability';
}

export function TypeSelectionView({
  inventoryTypes,
  loading,
  onTypeSelect,
  category
}: TypeSelectionViewProps) {
  const lastClickTimeRef = useRef<number>(0);

  // Group the types into their collapsible sections. Membership and section
  // order come from the canonical group definitions; within-group order stays
  // driven by the *_TYPE_ORDER constants (single ordering source).
  const groups = useMemo(
    () =>
      groupInventoryTypesByCategory(
        inventoryTypes.filter((type) => type && type.id),
        category
      ),
    [inventoryTypes, category]
  );

  const handleSelect = useCallback(
    (typeId: string) => {
      const now = Date.now();
      // Prevent rapid double-selects (debounce with 500ms)
      if (now - lastClickTimeRef.current < 500) {
        return;
      }
      lastClickTimeRef.current = now;

      if (typeId) {
        onTypeSelect(String(typeId));
      }
    },
    [onTypeSelect]
  );

  return (
    <>
      <div className="mb-6 space-y-1">
        <h2 className="sr-only">
          {category === 'liability' ? 'Add New Liability' : 'Add New Asset'}
        </h2>
        <p className="text-muted-foreground text-sm">
          {category === 'liability'
            ? 'Select the type of liability you want to add. Each type has specific fields to ensure accurate tracking.'
            : 'Select the type of asset you want to add to the estate inventory. Each type has specific fields to ensure accurate tracking.'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : groups.length > 0 ? (
        <div className="space-y-4 pb-2">
          {groups.map((group) => (
            /* Static group labels, not an accordion (VALE-1638): these sections
               are always expanded, so the shared CollapsibleSection no longer
               fits and is deliberately not used here. Its other consumers keep
               their collapse behaviour untouched. */
            <section key={group.key} data-testid={`section-${group.key}`}>
              <h3
                data-testid={`section-${group.key}-header`}
                className="text-muted-foreground border-border mb-2 border-b pb-1.5 text-sm font-medium tracking-wide uppercase"
              >
                {group.label}
              </h3>
              <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-2">
                {group.types.map((type) => {
                  const note =
                    type.description || INVENTORY_TYPE_DESCRIPTIONS[type.slug];
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleSelect(type.id)}
                      className="group hover:bg-accent flex w-full cursor-pointer items-center gap-3 rounded-sm px-4 py-2 text-left transition-colors"
                    >
                      <span className="bg-muted text-muted-foreground flex size-7 flex-none items-center justify-center rounded-md">
                        <InventoryIcon
                          assetType={
                            (type.slug || type.name).replace(
                              /-/g,
                              '_'
                            ) as AssetType
                          }
                          className="size-4"
                        />
                      </span>
                      <span className="flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden">
                        <span className="text-foreground text-sm font-medium whitespace-nowrap">
                          {type.name}
                        </span>
                        {note && (
                          <span className="text-muted-foreground truncate text-xs">
                            {note}
                          </span>
                        )}
                      </span>
                      <ChevronRight
                        aria-hidden="true"
                        className="text-muted-foreground size-4 flex-none opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="text-muted-foreground py-8 text-center">
          No inventory types available
        </div>
      )}
    </>
  );
}
